import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle, Clock, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { getAvailability, createServiceRequest } from '../services/api';

export const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    problem_description: ''
  });

  useEffect(() => {
    const fetchAvailability = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await getAvailability(dateStr);
        setAvailableSlots(response.data.available_slots);
      } catch (error) {
        console.error('Error fetching availability:', error);
        toast.error('Erreur lors du chargement des disponibilités');
      } finally {
        setLoadingSlots(false);
      }
    };

    if (selectedDate) {
      fetchAvailability();
    }
  }, [selectedDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.address || !formData.problem_description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await createServiceRequest({
        ...formData,
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        appointment_time: selectedTime
      });
      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès!');
    } catch (error) {
      console.error('Error submitting request:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfToday()) || date.getDay() === 0;
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a3a5c] mb-4">
            Rendez-vous confirmé!
          </h1>
          <p className="text-slate-600 mb-4">
            Votre demande de service a été enregistrée pour le:
          </p>
          <div className="bg-slate-50 rounded-xl p-4 mb-8">
            <p className="text-xl font-semibold text-[#1a3a5c]">
              {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
            <p className="text-[#00b4d8] font-medium">à {selectedTime}</p>
          </div>
          <p className="text-slate-500 text-sm mb-8">
            Un courriel de confirmation vous a été envoyé. Notre technicien vous contactera pour confirmer.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
            data-testid="back-home-btn"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Demande de service
          </h1>
          <p className="text-xl text-slate-300">
            Prenez rendez-vous pour une intervention rapide
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-8">
            {/* Left: Contact info */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                Vos informations
              </h2>
              
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Votre prénom"
                      className="mt-2 input-styled"
                      data-testid="first-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="mt-2 input-styled"
                      data-testid="last-name-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Courriel *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@courriel.com"
                    className="mt-2 input-styled"
                    data-testid="email-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(450) 555-1234"
                    className="mt-2 input-styled"
                    data-testid="phone-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address">Adresse de l'intervention *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Adresse complète"
                    className="mt-2 input-styled"
                    data-testid="address-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="problem_description">Description du problème *</Label>
                  <Textarea
                    id="problem_description"
                    name="problem_description"
                    value={formData.problem_description}
                    onChange={handleChange}
                    placeholder="Décrivez le problème rencontré..."
                    className="mt-2 input-styled min-h-[120px]"
                    data-testid="description-input"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Right: Calendar */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                <CalendarDays className="inline-block w-6 h-6 mr-2" />
                Choisissez votre rendez-vous
              </h2>

              <div className="flex justify-center mb-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  locale={fr}
                  className="rounded-xl border shadow-sm"
                  data-testid="calendar"
                />
              </div>

              {selectedDate && (
                <div className="mt-6">
                  <h3 className="font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Créneaux disponibles le {format(selectedDate, 'd MMMM', { locale: fr })}
                  </h3>
                  
                  {loadingSlots ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full mx-auto"></div>
                      <p className="text-slate-500 mt-2">Chargement...</p>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedTime(slot)}
                          className={`p-3 rounded-xl text-center font-medium transition-colors ${
                            selectedTime === slot
                              ? 'bg-[#00b4d8] text-white'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                          }`}
                          data-testid={`time-slot-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">
                      Aucun créneau disponible pour cette date
                    </p>
                  )}
                </div>
              )}

              {selectedDate && selectedTime && (
                <div className="mt-8 p-4 bg-[#00b4d8]/10 rounded-xl">
                  <p className="text-sm text-slate-600">Votre rendez-vous:</p>
                  <p className="font-semibold text-[#1a3a5c]">
                    {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })} à {selectedTime}
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-500">
              En transmettant ce formulaire, vous consentez à l’utilisation de vos renseignements pour traiter votre demande. Consultez notre <a href="/politique-confidentialite" className="text-[#0077b6] underline">politique de confidentialité</a>.
            </p>

              <Button
                type="submit"
                disabled={loading || !selectedDate || !selectedTime}
                className="w-full mt-6 rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white py-6 text-lg"
                data-testid="submit-btn"
              >
                {loading ? 'Envoi en cours...' : 'Confirmer le rendez-vous'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ServiceRequestPage;
